/**
 * Rewrites HTML `<details>` / `<summary>` collapsed sections embedded in
 * markdown into the Rovo-native boundary format that the rest of the stream
 * pipeline already understands.
 *
 *   <details><summary>Title</summary>Body</details>  →  ▞Title\nBody▚
 *
 * Downstream, the existing `expand` streamable component (see
 * `getStreamableComponents` in `chat-store/utils.ts`) picks up `▞…▚`, swaps
 * in the `[^expand^]` placeholder, and renders the body out-of-band through
 * `markdownData`.
 *
 * Spec reference: the HTML `<details>` element (rendered as a disclosure
 * widget) and its `<summary>` child, as documented by MDN and supported by
 * common markdown renderers.
 *
 * Known limitations (V1):
 *  - Code-fence aware rewriting is not implemented. A `<details>` block
 *    embedded inside a ``` code fence will still be rewritten. Agents
 *    producing collapsed sections generally don't do this, but if it becomes
 *    a real problem the fence-tracking state machine from
 *    `sanitize-markdown.ts` can be ported here.
 *  - Nested `<details>` are passed through unchanged.
 */

const DETAILS_OPEN_BOUNDARY = '▞';
const DETAILS_CLOSE_BOUNDARY = '▚';
const DEFAULT_TITLE = 'Details';

// Complete-tag matchers. Case-insensitive, allow attributes on open tags.
const DETAILS_OPEN_RE = /^<details(?:\s[^>]*)?>$/i;
const SUMMARY_OPEN_RE = /^<summary(?:\s[^>]*)?>$/i;
const SUMMARY_CLOSE_RE = /^<\/summary\s*>$/i;
const DETAILS_CLOSE_RE = /^<\/details\s*>$/i;

// Fast-path probe: if the text cannot possibly contain a relevant tag, skip
// the scan entirely.
const HAS_RELEVANT_TAG_RE = /<\/?(?:details|summary)\b/i;

type Mode = 'NORMAL' | 'IN_DETAILS_PRE_SUMMARY' | 'IN_SUMMARY' | 'IN_DETAILS_BODY';

type State = {
	mode: Mode;
	buffer: string;
};

export type DetailsPreprocessor = {
	/**
	 * Processes a chunk of streamed text. Returns the portion of output that
	 * is safe to emit. Any partial tag spanning a chunk boundary is buffered
	 * internally and surfaced on subsequent calls.
	 */
	process(chunk: string): string;
	/**
	 * Flushes any buffered partial tag and synthesises closers for
	 * unterminated `<details>` / `<summary>` blocks, so downstream never
	 * drops content.
	 */
	flush(): string;
};

/**
 * Shared scan used by both the streaming and one-shot paths.
 *
 * When `allowBuffer` is `true`, a trailing incomplete tag (i.e. a `<` with
 * no subsequent `>`) is stashed on `state.buffer` for the next call instead
 * of being emitted. When `false` (flush / one-shot), the partial text is
 * emitted as literal.
 */
const scan = (input: string, state: State, allowBuffer: boolean): string => {
	let out = '';
	let i = 0;

	const emitText = (text: string): void => {
		if (state.mode === 'IN_DETAILS_PRE_SUMMARY') {
			// Silently discard text between `<details>` and `<summary>` (in
			// practice this is whitespace / newlines the agent formats for
			// readability).
			return;
		}
		if (state.mode === 'IN_SUMMARY') {
			// Collapse newlines inside the summary so the downstream
			// `splitHeaderAndContent` (first-newline split) still sees a
			// single-line title.
			out += text.replace(/\r?\n/g, ' ');
			return;
		}
		out += text;
	};

	while (i < input.length) {
		const lt = input.indexOf('<', i);
		if (lt === -1) {
			emitText(input.slice(i));
			break;
		}
		if (lt > i) {
			emitText(input.slice(i, lt));
		}

		const gt = input.indexOf('>', lt);
		if (gt === -1) {
			if (allowBuffer) {
				state.buffer = input.slice(lt);
				return out;
			}
			// One-shot path: emit the dangling partial tag as literal.
			emitText(input.slice(lt));
			break;
		}

		const tag = input.slice(lt, gt + 1);

		if (DETAILS_OPEN_RE.test(tag)) {
			if (state.mode === 'NORMAL') {
				state.mode = 'IN_DETAILS_PRE_SUMMARY';
			} else {
				// Nested or unexpected — pass through so we don't eat user text.
				emitText(tag);
			}
		} else if (SUMMARY_OPEN_RE.test(tag)) {
			if (state.mode === 'IN_DETAILS_PRE_SUMMARY') {
				state.mode = 'IN_SUMMARY';
				out += DETAILS_OPEN_BOUNDARY;
			} else {
				emitText(tag);
			}
		} else if (SUMMARY_CLOSE_RE.test(tag)) {
			if (state.mode === 'IN_SUMMARY') {
				state.mode = 'IN_DETAILS_BODY';
				out += '\n';
			} else {
				emitText(tag);
			}
		} else if (DETAILS_CLOSE_RE.test(tag)) {
			if (state.mode === 'IN_DETAILS_BODY') {
				state.mode = 'NORMAL';
				out += DETAILS_CLOSE_BOUNDARY;
			} else if (state.mode === 'IN_DETAILS_PRE_SUMMARY') {
				// `<details>…</details>` with no `<summary>`. Synthesise a
				// default header so the renderer has something to show.
				state.mode = 'NORMAL';
				out += `${DETAILS_OPEN_BOUNDARY}${DEFAULT_TITLE}\n${DETAILS_CLOSE_BOUNDARY}`;
			} else if (state.mode === 'IN_SUMMARY') {
				// Malformed: `</details>` while still inside `<summary>`. Close
				// the summary implicitly and close the details.
				state.mode = 'NORMAL';
				out += `\n${DETAILS_CLOSE_BOUNDARY}`;
			} else {
				emitText(tag);
			}
		} else {
			// Unrelated tag (e.g. `<span>`, `<a href=…>`) — pass through.
			emitText(tag);
		}

		i = gt + 1;
	}

	state.buffer = '';
	return out;
};

const synthesiseClosers = (state: State): string => {
	let out = '';
	if (state.mode === 'IN_DETAILS_PRE_SUMMARY') {
		out += `${DETAILS_OPEN_BOUNDARY}${DEFAULT_TITLE}\n${DETAILS_CLOSE_BOUNDARY}`;
	} else if (state.mode === 'IN_SUMMARY') {
		out += `\n${DETAILS_CLOSE_BOUNDARY}`;
	} else if (state.mode === 'IN_DETAILS_BODY') {
		out += DETAILS_CLOSE_BOUNDARY;
	}
	state.mode = 'NORMAL';
	return out;
};

/**
 * One-shot (non-streaming) preprocessor. Any partial tag at the end of the
 * input is emitted as literal text since there is no subsequent chunk to
 * complete it.
 */
export const preprocessDetails = (text: string): string => {
	if (!text || !HAS_RELEVANT_TAG_RE.test(text)) {
		return text;
	}
	const state: State = { mode: 'NORMAL', buffer: '' };
	let out = scan(text, state, /* allowBuffer */ false);
	out += synthesiseClosers(state);
	return out;
};

/**
 * Stateful, streaming preprocessor. Safe to call once per streamed chunk.
 */
export const createDetailsPreprocessor = (): DetailsPreprocessor => {
	const state: State = { mode: 'NORMAL', buffer: '' };

	return {
		process(chunk: string): string {
			const combined = state.buffer + (chunk ?? '');
			state.buffer = '';
			// Fast-path: when we are not inside a details block and the combined
			// text contains no `<` at all, a tag is impossible — skip the scan.
			// We deliberately do NOT use a name-level regex here: a partial tag
			// like `<det` spanning a chunk boundary must still be buffered for
			// the next chunk to complete it.
			if (!combined || (state.mode === 'NORMAL' && !combined.includes('<'))) {
				return combined;
			}
			return scan(combined, state, /* allowBuffer */ true);
		},
		flush(): string {
			let out = '';
			if (state.buffer) {
				const leftover = state.buffer;
				state.buffer = '';
				out += scan(leftover, state, /* allowBuffer */ false);
			}
			out += synthesiseClosers(state);
			return out;
		},
	};
};
